import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterModule } from '@angular/router';
import { XpoButtonModule } from '@xpo-ltl/ngx-ltl-core/button';
import { XpoDownloadButtonModule } from '@xpo-ltl/ngx-ltl-core/download-button';
import { XpoIconModule } from '@xpo-ltl/ngx-ltl-core/icon';
import { Observable, of } from 'rxjs';
import { filter } from 'rxjs/operators';
import { BreadcrumbEnum } from '../../enums/breadcrumb.enum';
import { Breadcrumb } from '../../models/breadcrumb';
import { SpecDownloadAction, SpecDownloadService } from '../../services/spec-download.service';

@Component({
  selector: 'app-breadcrumbs',
  templateUrl: './breadcrumbs.component.html',
  styleUrls: ['./breadcrumbs.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'lau-BreadcrumbsContainer',
  },
  standalone: true,
  // XPO small-button styling applies to Material stroked/flat buttons (legacy button, as used by ngx-ltl-core).
  imports: [
    CommonModule,
    RouterModule,
    MatIconModule,
    MatLegacyButtonModule,
    XpoButtonModule,
    XpoDownloadButtonModule,
    XpoIconModule,
  ],
})
// TODO: This code has been copied from a npm library, try to refactor
export class BreadcrumbsComponent implements OnInit {
  breadcrumbs: Breadcrumb[] = [];
  ROUTE_DATA_BREADCRUMB: string = BreadcrumbEnum.BREADCRUMB;
  hasJustOneElement = true;
  serviceName = '';
  /** Right-aligned page action (swagger download) published by the current view. */
  readonly downloadAction$: Observable<SpecDownloadAction | null>;

  constructor(
    private router: Router,
    specDownload: SpecDownloadService
  ) {
    this.downloadAction$ = specDownload.current$;
  }

  ngOnInit(): void {
    // build breadcrumbs for the route already active on first load, since the initial
    // NavigationEnd may fire before this component subscribes to router events
    this.onRouteEvent();

    // subscribe to the NavigationEnd event
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((routeEvent) => {
      this.onRouteEvent();
    });
  }

  changeBreadcrumb(route: ActivatedRouteSnapshot, name: string) {
    const rootUrl = this.createRootUrl(route);
    const breadcrumb = this.breadcrumbs.find((bc) => {
      return bc.url === rootUrl;
    });

    if (!breadcrumb) {
      return;
    }

    breadcrumb.displayName = of(name);
  }

  private onRouteEvent() {
    let route = this.router.routerState.root.snapshot;
    let url = '';

    const newCrumbs = [];

    while (route.firstChild != null) {
      route = route.firstChild;

      if (route.routeConfig === null) {
        continue;
      }
      if (!route.routeConfig.path) {
        continue;
      }
      if (route.data && route.data.concat) {
        continue;
      }

      url += `/${this.createUrl(route)}`;
      newCrumbs.push(this.createBreadcrumb(route, url));
    }

    this.breadcrumbs = newCrumbs;
    this.hasJustOneElement = this.breadcrumbs.length <= 1;
  }

  private createBreadcrumb(route: ActivatedRouteSnapshot, url: string): Breadcrumb {
    return {
      displayName: this.getBreadcrumbLabel(route),
      terminal: this.isTerminal(route),
      url: url,
      route: route.routeConfig,
    };
  }

  private getBreadcrumbLabel(route: ActivatedRouteSnapshot): Observable<string> {
    switch (route.data[this.ROUTE_DATA_BREADCRUMB]) {
      case BreadcrumbEnum.API_STORE:
        return of(route.params[BreadcrumbEnum.APP_NAME]);
      default:
        return of(route.data[this.ROUTE_DATA_BREADCRUMB]);
    }
  }

  private isTerminal(route: ActivatedRouteSnapshot): boolean {
    return route.firstChild === null || route.firstChild.routeConfig === null || !route.firstChild.routeConfig.path;
  }

  private createUrl(route: ActivatedRouteSnapshot) {
    if (route.firstChild && route.firstChild.data && route.firstChild.data.concat) {
      return route.url
        .map(function (s) {
          return s.toString();
        })
        .concat(route.firstChild.url.toString())
        .join('/');
    }
    return route.url
      .map(function (s) {
        return s.toString();
      })
      .join('/');
  }

  private createRootUrl(route: ActivatedRouteSnapshot) {
    let url = '';
    let next = route.root;
    // this.serviceName = JSON.stringify(route.root.outlet);

    while (next.firstChild !== null) {
      next = next.firstChild;

      if (next.routeConfig === null) {
        continue;
      }
      if (!next.routeConfig.path) {
        continue;
      }

      url += `/${this.createUrl(next)}`;

      if (next === route) {
        break;
      }
    }

    return url;
  }
}
